import React, { useEffect, useState, useMemo } from "react";
import { Box, Stack, IconButton, Typography, TextField, MenuItem, Autocomplete, Grid, Button, Stepper, Step, StepLabel, Card, Divider, Snackbar, Alert, Chip, Paper, CircularProgress } from "@mui/material";
import { AssignmentOutlined, ListAltOutlined, RateReviewOutlined, DeleteOutline, AddCircleOutline, Schedule, PriorityHigh, Layers } from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { getData, postData } from "../../../../services/axiosInstance";
import { API_ENDPOINTS } from "../../../../services/endpoints";
import MissingDepartmentsDialog from '../../common/MissingDepartmentsDialog';

function CreateTask({ activeProject, epicData, onCancel, onSuccess, storyData, mode, onClose, isTaskManagement }) {
    const navigate = useNavigate();
    const initialSubtask = {
        title: "", department: null, team: null, assignee: [],
        startDate: "", endDate: "", estHours: "00", estMinutes: "00",
        priority: "Medium", description: ""
    };

    const [activeStep, setActiveStep] = useState(0);
    const [projectData, setProjectData] = useState(null);
    const [currentSubtask, setCurrentSubtask] = useState(initialSubtask);
    const [errors, setErrors] = useState({});
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "error" });
    const today = dayjs();
    const [projectsList, setProjectsList] = useState([]);
    const [selectedProject, setSelectedProject] = useState(null);
    const userDataRaw = localStorage.getItem('userData');
    const userData = JSON.parse(userDataRaw);
    const userType = userData?.user_type;
    const userId = userData?.id;
    const roleBasePath = userType === 'Team Lead' ? '/team-lead' : '/manager';
    // Add this with your other useState hooks
    const [apiErrorMessage, setApiErrorMessage] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showMissingDeptDialog, setShowMissingDeptDialog] = useState(false);
    const [selectedProjectDetails, setSelectedProjectDetails] = useState(null);

    const [formData, setFormData] = useState({
        title: "",
        taskType: "single",
        workType: "",
        description: "",
        startDate: "",
        endDate: "",
        estHours: "00",
        estMinutes: "00",
        priority: "Medium",
        department: null,
        team: null,
        assignee: [],
        subtasks: []
    });

    useEffect(() => {
        if (isTaskManagement && userId) {
            const fetchProjects = async () => {
                try {
                    let url = "";

                    if (userType === "Manager" || userType === "Team Lead") {
                        url = `${API_ENDPOINTS.fetchSupervisorProjects}/${userId}`;
                    }

                    if (url) {
                        const res = await getData(url);
                        if (res.status === "SUCCESS") {
                            setProjectsList(res.message.data || []);
                        }
                    }
                } catch (err) {
                    setProjectsList([]);
                }
            };
            fetchProjects();
        }
    }, [isTaskManagement, userId, userType]);

    const handleProjectSelect = async (project) => {
        setSelectedProject(project);
        if (project?.id) {
            try {
                const res = await getData(API_ENDPOINTS.fetchProjectById(project.id));

                if (res.status === "SUCCESS") {
                    const projectDetails = res.message.data || null;
                    setProjectData(projectDetails);
                    setSelectedProjectDetails(projectDetails);

                    // Check if departments are empty
                    const hasDepartments = projectDetails?.departments && projectDetails.departments.length > 0;
                    if (!hasDepartments) {
                        setShowMissingDeptDialog(true);
                    }
                }

                setFormData(prev => ({
                    ...prev,
                    department: null,
                    team: null,
                    assignee: [],
                    startDate: dayjs().format('YYYY-MM-DD'), // Default to Current Date
                    endDate: dayjs().format('YYYY-MM-DD')
                }));
            } catch (error) {
                setSnackbar({ open: true, message: 'Failed to load project details', severity: 'error' });
            }
        }
    };

    const handleRefreshProjectDetails = async () => {
        if (!selectedProject?.id) return;
        try {
            const res = await getData(API_ENDPOINTS.fetchProjectById(selectedProject.id));
            if (res.status === "SUCCESS") {
                const projectDetails = res.message.data || null;
                setProjectData(projectDetails);
                setSelectedProjectDetails(projectDetails);
            }
        } catch (error) {
            // Handle error silently
        }
    };

    useEffect(() => {
        const targetProjectId = epicData?.project_id || activeProject?.id || storyData?.project_id;
        if (targetProjectId) {
            getData(API_ENDPOINTS.fetchProjectById(targetProjectId))
                .then((res) => {
                    const projectDetails = res?.message?.data || null;
                    setProjectData(projectDetails);
                    setSelectedProjectDetails(projectDetails);

                    // Check if departments are empty
                    const hasDepartments = projectDetails?.departments && projectDetails.departments.length > 0;
                    if (!hasDepartments) {
                        setShowMissingDeptDialog(true);
                    }
                });
        }
    }, [epicData?.project_id, activeProject?.id, storyData?.project_id]);

    const departmentOptions = useMemo(() => projectData?.departments || [], [projectData]);
    const getTeamsForDept = (deptObj) => deptObj?.teams || [];
    const getMembersForTeam = (teamObj) =>
        teamObj?.members?.map(m => ({ ...m, label: `${m.first_name} ${m.last_name}` })) || [];

    const steps = formData.taskType === "single"
        ? ['Task Details', 'Task Assignment', 'Task Review']
        : ['Task Details', 'Subtasks', 'Task Review'];

    const capitalizeFirstLetter = (string) => string.charAt(0).toUpperCase() + string.slice(1);

    const getMaxHours = (start, end, type) => {
        // For single tasks, per day limit is 8 hours
        if (type === 'single') return 8;
        if (!start || !end) return 8;

        let startDate = dayjs(start);
        let endDate = dayjs(end);

        if (endDate.isBefore(startDate)) return 8;

        // Total capacity = number of calendar days selected * 8 hours
        const totalDays = endDate.diff(startDate, 'day') + 1;
        return totalDays * 8;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        let processedValue = value;
        if (name === "title" || name === "description") processedValue = capitalizeFirstLetter(value);

        setFormData(prev => {
            const newData = { ...prev, [name]: processedValue };

            // Force End Date to match Start Date for Single Tasks
            if (name === "startDate" && prev.taskType === "single") {
                newData.endDate = processedValue;
            }

            // Also sync if the user switches taskType to 'single' while a startDate exists
            if (name === "taskType" && processedValue === "single" && prev.startDate) {
                newData.endDate = prev.startDate;
            }

            if (name === "estHours") {
                const currentMax = getMaxHours(newData.startDate, newData.endDate, newData.taskType);
                const selectedH = parseInt(processedValue);

                if (selectedH === currentMax) {
                    // Force reset because we hit the ceiling
                    newData.estMinutes = "00";
                }
            }

            if (name === "estHours" && processedValue === "08") {
                newData.estMinutes = "00";
            }

            return newData;
        });
    };

    const handleSubtaskChange = (name, value) => {
        let processedValue = value;
        if (name === "title" || name === "description") processedValue = capitalizeFirstLetter(value);

        setCurrentSubtask(prev => {
        const updated = { ...prev, [name]: name === 'assignee' ? (processedValue || []) : processedValue };
            if (name === 'estHours') {
                const currentMax = getMaxHours(updated.startDate, updated.endDate, 'multiple');
                if (parseInt(processedValue) === currentMax) {
                    updated.estMinutes = "00";
                }
            }
            return updated;
        });

        if (errors[`sub_${name}`]) setErrors(prev => ({ ...prev, [`sub_${name}`]: "" }));
    };

    const validateForm = () => {
        let tempErrors = {};
        const currentLabel = steps[activeStep];

        if (currentLabel === 'Task Details') {
            if (isTaskManagement && !selectedProject) tempErrors.project = "Project is required";
            if (!formData.title.trim()) tempErrors.title = "Task Title is required";
            if (!formData.workType) tempErrors.workType = "Work Type is required";
            if (formData.taskType === "multiple") {
                if (!formData.startDate) tempErrors.startDate = "Start Date is required";
                if (!formData.endDate) tempErrors.endDate = "End Date is required";
                // Parent-level hours are not collected for Multiple Tasks — effort is tracked per subtask instead.
            }
        }

        if (currentLabel === 'Task Assignment') {
            if (!formData.department) tempErrors.department = "Department is required";
            if (!formData.team) tempErrors.team = "Team is required";
            if (!formData.assignee || formData.assignee.length === 0) tempErrors.assignee = "Team member is required";
            if (!formData.startDate) tempErrors.startDate = "Start Date is required";
            if (!formData.endDate) tempErrors.endDate = "End Date is required";
            if (formData.estHours === "00" && formData.estMinutes === "00") {
                tempErrors.estHours = "Required";
                setSnackbar({ open: true, message: "Estimated time cannot be 0 hours", severity: "error" });
            }
        }

        if (currentLabel === 'Subtasks' && formData.subtasks.length === 0) {
            tempErrors.subtasks = "Please add at least one subtask";
            setSnackbar({ open: true, message: "Please add at least one subtask before proceeding!", severity: "error" });
        }

        setErrors(tempErrors);
        return Object.keys(tempErrors).length === 0;
    };

    const handleAddSubtask = () => {
        let subErrors = {};
        if (!currentSubtask.title.trim()) subErrors.sub_title = "Title required";
        if (!currentSubtask.department) subErrors.sub_department = "Department is required";
        if (!currentSubtask.team) subErrors.sub_team = "Team is required";
        if (!currentSubtask.assignee || currentSubtask.assignee.length === 0) subErrors.sub_assignee = "Team member is required";
        if (!currentSubtask.startDate) subErrors.sub_startDate = "Start Date required";
        if (!currentSubtask.endDate) subErrors.sub_endDate = "End Date required";
        if (currentSubtask.estHours === "00" && currentSubtask.estMinutes === "00") {
            setSnackbar({ open: true, message: "Subtask hours must be more than zero", severity: "error" });
            subErrors.sub_estHours = "Required";
        }

        if (Object.keys(subErrors).length === 0) {
            setFormData(prev => ({ ...prev, subtasks: [...prev.subtasks, currentSubtask] }));
            setCurrentSubtask(initialSubtask);
            setErrors({});
        } else {
            setErrors(subErrors);
        }
    };

    const calculateHours = (h, m) => parseFloat(h) + (parseFloat(m) / 60) || 0;

    const handleNextStep = async () => {
        if (validateForm()) {
            if (activeStep < steps.length - 1) setActiveStep((prev) => prev + 1);
            else await handleSubmit();
        }
    };

    const timelineInfo = useMemo(() => {
        let start, end, label;

        if (epicData?.start_date) {
            start = epicData.start_date;
            end = epicData.end_date;
            label = "Epic";
        } else if (activeProject?.start_date) {
            start = activeProject.start_date;
            end = activeProject.end_date;
            label = "Project";
        } else if (storyData?.start_date) {
            start = storyData.start_date;
            end = storyData.due_date;
            label = "Story";
        } else if (isTaskManagement && selectedProject) {
            start = selectedProject.start_date;
            end = selectedProject.end_date;
            label = "Project";
        }

        if (start && end) {
            return {
                label,
                start: dayjs(start),
                end: dayjs(end)
            };
        }

        return null;
    }, [epicData, activeProject, storyData, isTaskManagement, selectedProject]);

    const handleSubmit = async () => {
        setApiErrorMessage("");
        setIsSubmitting(true);
        const userDataRaw = localStorage.getItem('userData');
        const userData = userDataRaw ? JSON.parse(userDataRaw) : null;
        const finalProjectId =
            epicData?.project_id ||
            activeProject?.id ||
            storyData?.project_id ||
            selectedProject?.id;

        const finalProjectName =
            epicData?.project_title ||
            activeProject?.project_title ||
            storyData?.projectDetails?.project_title ||
            selectedProject?.project_title;

        if (!finalProjectId) {
            setSnackbar({ open: true, message: "Project ID is missing. Please select a project.", severity: "error" });
            return;
        }

        try {
            const taskPayload = {
                project_id: finalProjectId,
                epic_id: epicData?.id || storyData?.epic_id || null,
                user_story_id: storyData?.id || null,
                task_type: formData.taskType === 'single' ? "Single" : "Multiple",
                manager_id: userData?.id ? parseInt(userData.id) : null,
                task_title: formData.title,
                description: formData.description,
                priority: formData.priority,
                start_date: formData.startDate,
                due_date: formData.endDate,
                estimated_hours: calculateHours(formData.estHours, formData.estMinutes),
                work_type: formData.workType
            };

            // 4. Handle Single vs Multiple specifics
            if (formData.taskType === "single") {
                taskPayload.department_id = formData.department?.department_id;
                taskPayload.team_id = formData.team?.team_id;
                // Add check for assignee existence
                taskPayload.assignees = (formData.assignee || []).map(a => a.id);
            } else {
                // Add check for subtasks existence
                taskPayload.subtasks = (formData.subtasks || []).map(st => ({
                    task_title: st.title,
                    description: st.description || "",
                    priority: st.priority,
                    start_date: st.startDate,
                    due_date: st.endDate,
                    estimated_hours: calculateHours(st.estHours, st.estMinutes),
                    department_id: st.department?.department_id,
                    team_id: st.team?.team_id,
                    // Safety check for subtask assignees
                    assignees: st.assignee ? st.assignee.map(a => a.id) : []
                }));
            }

            // 5. API Call
            const response = await postData(API_ENDPOINTS.createTask, taskPayload);

            // Handle specific API failure structures
            if (response && response.status === "SUCCESS") {
                setSnackbar({ open: true, message: "Task created successfully!", severity: "success" });
                setTimeout(() => {
                    if (mode === 'getStory' && onClose) return onClose(true);
                    if (onSuccess) onSuccess();
                    if (onCancel) onCancel();
                    if (activeProject) {
                        navigate(`${roleBasePath}/user-tasks?projectId=${finalProjectId}&projectTitle=${finalProjectName}&origin="Project"`);
                    } else if (epicData) {
                        navigate(`${roleBasePath}/user-tasks?projectId=${epicData.project_id}&projectTitle=${epicData.project_title}&epicId=${epicData.id}&origin=Epic`)
                    } else if (isTaskManagement === true) {
                        if (onClose) onClose();
                    } else {
                        navigate(`${roleBasePath}/user-tasks?projectId=${finalProjectId}&projectTitle=${finalProjectName}&origin="Story"`);
                    }
                }, 600);
            } else {
                const errorMsg = response?.message || response?.data?.message || "Server rejected the request";

                setApiErrorMessage(errorMsg);
                setSnackbar({ open: true, message: "Submission Failed", severity: "error" });
            }
        } catch (err) {
            const catchErrorMsg = err.response?.data?.message || err.message || "An unexpected error occurred";
            setApiErrorMessage(catchErrorMsg);
        } finally {
            setIsSubmitting(false);
        }
    };

    const ReviewItem = ({ label, value, icon }) => (
        <Box sx={{ mb: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ color: '#64748b', mb: 0.5 }}>
                {icon}
                <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>{label}</Typography>
            </Stack>
            <Typography variant="body2" sx={{ color: '#1e293b', fontWeight: 500, ml: 3 }}>
                {value || 'N/A'}
            </Typography>
        </Box>
    );

    const getStepContent = (step) => {
        const currentStepLabel = steps[step];
        const isSubtaskStep = currentStepLabel === 'Subtasks';
        const targetData = isSubtaskStep ? currentSubtask : formData;

        const setter = (name, value) => {
            if (isSubtaskStep) {
                handleSubtaskChange(name, value);
            } else {
                setFormData(prev => {
                    const updated = { ...prev, [name]: name === 'assignee' ? (value || []) : value };
                    // Logic: Sync End Date with Start Date for Single Tasks
                    if (name === 'startDate' && prev.taskType === 'single') {
                        updated.endDate = value;
                    }

                    // Logic: Reset minutes if hours hit the 8h limit
                    if (name === 'estHours' && value === '08') {
                        updated.estMinutes = '00';
                    }
                    return updated;
                });
                if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
            }
        };
        const maxAllowedHours = getMaxHours(targetData.startDate, targetData.endDate, isSubtaskStep ? 'multiple' : formData.taskType);

        const getDateHelperText = (type) => {
            if (!timelineInfo) return "";
            const label = timelineInfo.label;
            const date = type === 'start' ? timelineInfo.start : timelineInfo.end;
            if (!date) return "";
            return `${label} ${type === 'start' ? 'Start' : 'End'}: ${dayjs(date).format('DD/MM/YYYY')}`;
        };

        if (currentStepLabel === 'Task Details') {
            return (
                <Grid container spacing={3}>
                    {isTaskManagement && (
                        <Grid size={{ xs: 12 }}>
                            <Autocomplete
                                options={projectsList}
                                getOptionLabel={(opt) => opt.project_title || ""}
                                value={selectedProject}
                                onChange={(_, val) => handleProjectSelect(val)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Select Project *"
                                        size="small"
                                        error={!!errors.project}
                                        helperText={errors.project}
                                    />
                                )}
                            />
                        </Grid>
                    )}
                    <Grid size={{ xs: 12 }}>
                        <TextField fullWidth label="Task Title *" name="title" value={formData.title} onChange={handleInputChange} error={!!errors.title} helperText={errors.title} size="small" />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <TextField select fullWidth label="Task Type *" name="taskType" value={formData.taskType} onChange={handleInputChange} size="small" >
                            <MenuItem value="single">Single Task</MenuItem>
                            <MenuItem value="multiple">Multiple Task</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <TextField select fullWidth label="Work Type *" name="workType" value={formData.workType} onChange={handleInputChange} error={!!errors.workType} helperText={errors.workType} size="small" >
                            <MenuItem value="Bug">Bug</MenuItem>
                            <MenuItem value="Enhancement">Enhancement</MenuItem>
                            <MenuItem value="Feature">Feature</MenuItem>
                        </TextField>
                    </Grid>
                    {formData.taskType === "multiple" && (
                        <>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <DatePicker
                                    label="Start Date *"
                                    format="DD/MM/YYYY"
                                    value={targetData.startDate ? dayjs(targetData.startDate) : null}
                                    onChange={(nv) => setter('startDate', nv ? nv.format('YYYY-MM-DD') : "")}
                                    minDate={today}
                                    // Limit selection to project end date, unless that end date has
                                    // already passed (which would otherwise invert the range and disable every day)
                                    maxDate={timelineInfo && dayjs(timelineInfo.end).isAfter(today) ? dayjs(timelineInfo.end) : null}
                                    slotProps={{
                                        textField: {
                                            size: 'small',
                                            fullWidth: true,
                                            // 1. Mark red if there is a validation error
                                            error: !!errors[isSubtaskStep ? 'sub_startDate' : 'startDate'],
                                            // 2. Priority: Show validation error message if exists, 
                                            //    else show the Project Timeline info, else nothing.
                                            helperText: errors[isSubtaskStep ? 'sub_startDate' : 'startDate']
                                                || getDateHelperText('start')
                                                || ""
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <DatePicker
                                    label="End Date *"
                                    format="DD/MM/YYYY"
                                    value={targetData.endDate ? dayjs(targetData.endDate) : null}
                                    // Disable interaction if it's a single task (since it's locked to Start Date)
                                    disabled={formData.taskType === "single"}
                                    onChange={(nv) => setter('endDate', nv ? nv.format('YYYY-MM-DD') : "")}

                                    // --- UPDATED LOGIC ---
                                    // 1. Prevent selecting any date before the Start Date
                                    // 2. If no Start Date is selected yet, fall back to 'today'
                                    minDate={targetData.startDate ? dayjs(targetData.startDate) : today}

                                    // 3. Prevent selecting any date after the Project/Epic End Date,
                                    // unless that end date is before the min bound above (already elapsed)
                                    maxDate={timelineInfo && dayjs(timelineInfo.end).isAfter(targetData.startDate ? dayjs(targetData.startDate) : today) ? dayjs(timelineInfo.end) : null}
                                    // ---------------------

                                    slotProps={{
                                        textField: {
                                            size: 'small',
                                            fullWidth: true,
                                            error: !!errors[isSubtaskStep ? 'sub_endDate' : 'endDate'],
                                            helperText: formData.taskType === "single"
                                                ? "Locked to Start Date for Single Tasks"
                                                : (errors[isSubtaskStep ? 'sub_endDate' : 'endDate'] || getDateHelperText('end'))
                                        }
                                    }}
                                />
                            </Grid>
                            {/* Parent-level Hours/Mins are not collected for Multiple Tasks — effort is tracked per subtask instead.
                            <Grid size={{ xs: 12, md: 6 }}>
                                <Stack direction="row" spacing={1}>
                                    <TextField select fullWidth label="Hours" name="estHours" value={formData.estHours} onChange={handleInputChange} size="small" error={!!errors.estHours}>
                                        {[...Array(maxAllowedHours + 1)].map((_, i) => (
                                            <MenuItem key={i} value={i < 10 ? `0${i}` : `${i}`}>{i}h</MenuItem>
                                        ))}
                                    </TextField>
                                    <TextField
                                        key={`mins-field-${targetData.estHours}-${maxAllowedHours}`}
                                        select
                                        fullWidth
                                        label="Mins"
                                        size="small"
                                        value={targetData.estMinutes}
                                        onChange={(e) => setter('estMinutes', e.target.value)}
                                    >
                                        {["00", "15", "30", "45"].map(m => {
                                            const isMaxHourSelected = parseInt(targetData.estHours) === maxAllowedHours;

                                            return (
                                                <MenuItem
                                                    key={m}
                                                    value={m}
                                                    // Only disable 15, 30, 45 if we are at the hour limit
                                                    disabled={isMaxHourSelected && m !== "00"}
                                                >
                                                    {m}m
                                                </MenuItem>
                                            );
                                        })}
                                    </TextField>
                                </Stack>
                            </Grid>
                            */}
                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField select fullWidth label="Priority" name="priority" value={formData.priority} onChange={handleInputChange} size="small">
                                    <MenuItem value="Low">Low</MenuItem><MenuItem value="Medium">Medium</MenuItem><MenuItem value="High">High</MenuItem>
                                </TextField>
                            </Grid>
                        </>
                    )}
                    <Grid size={{ xs: 12 }}>
                        <TextField fullWidth multiline rows={4} label="Description" name="description" value={formData.description} onChange={handleInputChange} />
                    </Grid>
                </Grid>
            );
        }

        if (currentStepLabel === 'Task Assignment' || currentStepLabel === 'Subtasks') {
            // Add this inside the Subtasks step logic
            const isSubtaskFormComplete =
                currentSubtask.title.trim() !== "" &&
                currentSubtask.department !== null &&
                currentSubtask.team !== null &&
                currentSubtask.assignee.length > 0 &&
                currentSubtask.startDate !== "" &&
                currentSubtask.endDate !== "" &&
                !(currentSubtask.estHours === "00" && currentSubtask.estMinutes === "00");
            return (
                <Grid container spacing={3}>
                    {/* --- 1. FORM MOVED TO TOP --- */}
                    <Grid size={{ xs: 12 }}>
                        <Typography variant="subtitle2" sx={{ mb: 2, color: '#475569', fontWeight: 700 }}>
                            {isSubtaskStep ? "Subtask Details" : "Assignment Details"}
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12 }}>
                                <TextField
                                    fullWidth
                                    label={isSubtaskStep ? "Subtask Title *" : "Task Title *"}
                                    size="small"
                                    value={targetData.title}
                                    onChange={(e) => setter('title', e.target.value)}
                                    error={!!(isSubtaskStep ? errors.sub_title : errors.title)}
                                    helperText={isSubtaskStep ? errors.sub_title : errors.title}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <Autocomplete
                                    options={departmentOptions}
                                    getOptionLabel={(opt) => opt.department_name || ""}
                                    value={targetData.department}
                                    onChange={(_, val) => setter('department', val)}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Department *"
                                            size="small"
                                            error={!!(isSubtaskStep ? errors.sub_department : errors.department)}
                                            helperText={isSubtaskStep ? errors.sub_department : errors.department}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <Autocomplete
                                    disabled={!targetData.department}
                                    options={getTeamsForDept(targetData.department)}
                                    getOptionLabel={(opt) => opt.team_name || ""}
                                    value={targetData.team}
                                    onChange={(_, val) => setter('team', val)}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Team *"
                                            size="small"
                                            error={!!(isSubtaskStep ? errors.sub_team : errors.team)}
                                            helperText={isSubtaskStep ? errors.sub_team : errors.team}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <Autocomplete
                                    multiple
                                    disabled={!targetData.team}
                                    options={getMembersForTeam(targetData.team)}
                                    getOptionLabel={(opt) => opt.label || ""}
                                    value={targetData.assignee || []}
                                    onChange={(_, val) => setter('assignee', val)}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Team Members *"
                                            size="small"
                                            error={!!(isSubtaskStep ? errors.sub_assignee : errors.assignee)}
                                            helperText={isSubtaskStep ? errors.sub_assignee : errors.assignee}
                                        />
                                    )}
                                    renderTags={(tagValue, getTagProps) =>
                                        tagValue.map((option, index) => {
                                            const { key, ...tagProps } = getTagProps({ index });
                                            return <Chip key={key} {...tagProps} label={option.label} size="small" />;
                                        })
                                    }
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <DatePicker
                                    label="Start Date *"
                                    format="DD/MM/YYYY"
                                    value={targetData.startDate ? dayjs(targetData.startDate) : null}
                                    onChange={(nv) => setter('startDate', nv ? nv.format('YYYY-MM-DD') : "")}
                                    minDate={formData.startDate ? dayjs(formData.startDate) : today}
                                    // For subtasks, cap to the parent task's own end date. For the top-level
                                    // single-task's own Start Date, formData.endDate just mirrors this same
                                    // field (see the single-task sync above) — capping by it would collapse
                                    // the calendar to one selectable day, so fall back to the project/epic
                                    // timeline instead.
                                    maxDate={isSubtaskStep ? (formData.endDate ? dayjs(formData.endDate) : null) : (timelineInfo ? dayjs(timelineInfo.end) : null)}
                                    slotProps={{
                                        textField: {
                                            size: 'small',
                                            fullWidth: true,
                                            error: !!(isSubtaskStep ? errors.sub_startDate : errors.startDate),
                                            helperText: (isSubtaskStep ? errors.sub_startDate : errors.startDate) || getDateHelperText('start') || ""
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <DatePicker
                                    label="End Date *"
                                    format="DD/MM/YYYY"
                                    value={targetData.endDate ? dayjs(targetData.endDate) : null}
                                    onChange={(nv) => setter('endDate', nv ? nv.format('YYYY-MM-DD') : "")}
                                    minDate={targetData.startDate ? dayjs(targetData.startDate) : today}
                                    maxDate={isSubtaskStep ? (formData.endDate ? dayjs(formData.endDate) : null) : (timelineInfo ? dayjs(timelineInfo.end) : null)}
                                    slotProps={{
                                        textField: {
                                            size: 'small',
                                            fullWidth: true,
                                            error: !!(isSubtaskStep ? errors.sub_endDate : errors.endDate),
                                            helperText: (isSubtaskStep ? errors.sub_endDate : errors.endDate) || getDateHelperText('end') || ""
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <Stack direction="row" spacing={1}>
                                    <TextField
                                        select
                                        fullWidth
                                        label="Hours"
                                        size="small"
                                        value={targetData.estHours}
                                        onChange={(e) => setter('estHours', e.target.value)}
                                        error={!!(isSubtaskStep ? errors.sub_estHours : errors.estHours)}
                                        helperText={isSubtaskStep ? errors.sub_estHours : errors.estHours}
                                    >
                                        {[...Array(maxAllowedHours + 1)].map((_, i) => (
                                            <MenuItem key={i} value={i < 10 ? `0${i}` : `${i}`}>{i}h</MenuItem>
                                        ))}
                                    </TextField>
                                    <TextField select fullWidth label="Mins" size="small" value={targetData.estMinutes} onChange={(e) => setter('estMinutes', e.target.value)}>
                                        {["00", "15", "30", "45"].map(m => (
                                            <MenuItem key={m} value={m} disabled={parseInt(targetData.estHours) === maxAllowedHours && m !== "00"}>{m}m</MenuItem>
                                        ))}
                                    </TextField>
                                </Stack>
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField select fullWidth label="Priority" size="small" value={targetData.priority} onChange={(e) => setter('priority', e.target.value)}>
                                    {["Low", "Medium", "High"].map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                                </TextField>
                            </Grid>
                            {isSubtaskStep && (
                                <Grid size={{ xs: 12 }}>
                                    <Button
                                        fullWidth
                                        // If complete, use success color, otherwise use primary
                                        variant="contained"
                                        color={isSubtaskFormComplete ? "success" : "primary"}
                                        startIcon={<AddCircleOutline />}
                                        onClick={handleAddSubtask}
                                        sx={{
                                            mt: 1,
                                            textTransform: 'none',
                                            // Optional: add a slight pulse animation when complete
                                            transition: 'all 0.3s ease'
                                        }}
                                    >
                                        {isSubtaskFormComplete ? "Confirm to Add Task" : "Add Subtask to List"}
                                    </Button>
                                </Grid>
                            )}
                        </Grid>
                    </Grid>

                    {/* --- 2. LIST MOVED TO BOTTOM --- */}
                    {isSubtaskStep && formData.subtasks.length > 0 && (
                        <Grid size={{ xs: 12 }}>
                            <Divider sx={{ my: 3 }}><Chip label={`ADDED SUBTASKS (${formData.subtasks.length})`} size="small" /></Divider>
                            <Stack spacing={2}>
                                {formData.subtasks.map((st, idx) => (
                                    <Card key={idx} variant="outlined" sx={{ borderRadius: '12px', bgcolor: '#f8fafc' }}>
                                        <Box sx={{ p: 2 }}>
                                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                <Box>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{st.title}</Typography>
                                                    <Typography variant="caption" color="textSecondary">
                                                        {st.department?.department_name} • {st.team?.team_name} • {st.estHours}h {st.estMinutes}m
                                                    </Typography>
                                                </Box>
                                                <Stack direction="row" spacing={1}>
                                                    <IconButton onClick={() => { setCurrentSubtask(st); setFormData(p => ({ ...p, subtasks: p.subtasks.filter((_, i) => i !== idx) })); }} size="small" color="primary">
                                                        <RateReviewOutlined fontSize="small" />
                                                    </IconButton>
                                                    <IconButton onClick={() => setFormData(p => ({ ...p, subtasks: p.subtasks.filter((_, i) => i !== idx) }))} size="small" color="error">
                                                        <DeleteOutline fontSize="small" />
                                                    </IconButton>
                                                </Stack>
                                            </Stack>
                                        </Box>
                                    </Card>
                                ))}
                            </Stack>
                        </Grid>
                    )}
                </Grid>
            );
        }

        if (currentStepLabel === 'Task Review') {
            return (
                <Box>
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>Review Task Details</Typography>
                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12 }}>
                            <Paper variant="outlined" sx={{ p: 3, borderRadius: '16px', bgcolor: '#f8fafc' }}>
                                <Typography variant="overline" sx={{ color: '#8b5cf6', fontWeight: 800 }}>Core Information</Typography>
                                <Grid container spacing={2} sx={{ mt: 1 }}>
                                    <Grid size={{ xs: 12, md: 6 }}><ReviewItem label="Title" value={formData.title} icon={<AssignmentOutlined fontSize="small" />} /></Grid>
                                    <Grid size={{ xs: 12, md: 6 }}><ReviewItem label="Work Type" value={formData.workType} icon={<Layers fontSize="small" />} /></Grid>
                                    <Grid size={{ xs: 12 }}><ReviewItem label="Description" value={formData.description} icon={<ListAltOutlined fontSize="small" />} /></Grid>
                                </Grid>
                            </Paper>
                        </Grid>

                        {formData.taskType === 'single' ? (
                            <Grid size={{ xs: 12 }}>
                                <Paper variant="outlined" sx={{ p: 3, borderRadius: '16px' }}>
                                    <Typography variant="overline" sx={{ color: '#8b5cf6', fontWeight: 800 }}>Assignment & Schedule</Typography>
                                    <Grid container spacing={2} sx={{ mt: 1 }}>
                                        <Grid size={{ xs: 12, md: 4 }}><ReviewItem label="Dept / Team" value={`${formData.department?.department_name} / ${formData.team?.team_name}`} icon={<Layers fontSize="small" />} /></Grid>
                                        <Grid size={{ xs: 12, md: 4 }}><ReviewItem label="Timeline" value={`${formData.startDate} to ${formData.endDate}`} icon={<Schedule fontSize="small" />} /></Grid>
                                        <Grid size={{ xs: 12, md: 4 }}><ReviewItem label="Effort / Priority" value={`${formData.estHours}h ${formData.estMinutes}m | ${formData.priority}`} icon={<PriorityHigh fontSize="small" />} /></Grid>
                                        <Grid size={{ xs: 12 }}>
                                            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>ASSIGNEES</Typography>
                                            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                                {(formData.assignee || []).map((a, i) => <Chip key={i} label={a.label} size="small" sx={{ bgcolor: '#ede9fe' }} />)}
                                            </Stack>
                                        </Grid>
                                    </Grid>
                                </Paper>
                            </Grid>
                        ) : (
                            <Grid size={{ xs: 12 }}>
                                <Typography variant="overline" sx={{ color: '#8b5cf6', fontWeight: 800, ml: 1 }}>Subtask Breakdown ({formData.subtasks.length})</Typography>
                                <Stack spacing={2} sx={{ mt: 1 }}>
                                    {formData.subtasks.map((st, i) => (
                                        <Paper key={i} variant="outlined" sx={{ p: 2, borderRadius: '12px' }}>
                                            <Stack direction="row" justifyContent="space-between">
                                                <Box>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{st.title}</Typography>
                                                    <Typography variant="caption" color="textSecondary">
                                                        {st.department?.department_name} • {st.team?.team_name} • {st.priority} Priority
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ textAlign: 'right' }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{st.estHours}h {st.estMinutes}m</Typography>
                                                    <Typography variant="caption" display="block">{st.startDate} - {st.endDate}</Typography>
                                                </Box>
                                            </Stack>
                                            {(st.assignee || []).length > 0 && (
                                                <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap', rowGap: 1 }}>
                                                    {st.assignee.map((a, ai) => (
                                                        <Chip key={ai} label={a.label} size="small" sx={{ bgcolor: '#ede9fe' }} />
                                                    ))}
                                                </Stack>
                                            )}
                                        </Paper>
                                    ))}
                                </Stack>
                            </Grid>
                        )}
                    </Grid>
                    <Box my={3}>
                        {apiErrorMessage && (
                            <Alert
                                severity="error"
                                sx={{ mb: 3, borderRadius: '12px', fontWeight: 600 }}
                            >
                                {apiErrorMessage}
                            </Alert>
                        )}
                    </Box>
                </Box>
            );
        }
        return null;
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ width: '100%', bgcolor: '#fff' }}>
                <Box sx={{ width: '100%', py: { xs: 1.5, sm: 4 }, bgcolor: '#f8fafc' }}>
                    <Stepper activeStep={activeStep} alternativeLabel>
                        {steps.map((label, index) => (
                            <Step key={label} completed={activeStep > index}>
                                <StepLabel><Typography sx={{ fontWeight: activeStep === index ? 700 : 500 }}>{label}</Typography></StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                </Box>
                <Box sx={{ p: { xs: 1, sm: 4 }, display: 'flex', justifyContent: 'center' }}>
                    <Box sx={{ width: '100%', maxWidth: 900 }}>
                        <Box sx={{ minHeight: 300 }}>{getStepContent(activeStep)}</Box>
                        <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{ mt: 4 }}>
                            <Button disabled={activeStep === 0} onClick={() => {
                                setActiveStep(p => p - 1);
                                setApiErrorMessage("");
                            }} sx={{ textTransform: 'none' }}>Back</Button>
                            <Button
                                variant="contained"
                                onClick={handleNextStep}
                                // Disable button while api is calling
                                disabled={isSubmitting}
                                sx={{ bgcolor: '#8b5cf6', '&:hover': { bgcolor: '#7c3aed' }, textTransform: 'none', minWidth: 120 }}
                            >
                                {isSubmitting ? (
                                    <CircularProgress size={24} sx={{ color: "white" }} />
                                ) : (
                                    activeStep === steps.length - 1 ? 'Confirm & Create' : 'Next'
                                )}
                            </Button>
                        </Stack>
                    </Box>
                </Box>
                <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                    <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
                </Snackbar>

                <MissingDepartmentsDialog
                    open={showMissingDeptDialog}
                    project={selectedProjectDetails}
                    onClose={() => setShowMissingDeptDialog(false)}
                    onDepartmentsAdded={handleRefreshProjectDetails}
                />
            </Box>
        </LocalizationProvider>
    );
}

export default CreateTask;